import { initializeApp, deleteApp } from 'firebase/app';
import {
    getAuth, setPersistence, browserSessionPersistence, inMemoryPersistence, createUserWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, signOut, GoogleAuthProvider, onAuthStateChanged, onIdTokenChanged, FacebookAuthProvider, OAuthProvider, sendPasswordResetEmail, sendEmailVerification, applyActionCode, checkActionCode, confirmPasswordReset, verifyPasswordResetCode,
    EmailAuthProvider, reauthenticateWithCredential, updatePassword,
} from 'firebase/auth';
import { createContext, useEffect, useState } from "react";

const firebaseConfigStr = import.meta.env.VITE_FIREBASE_CONFIG;
const firebaseConfig = firebaseConfigStr ? JSON.parse(firebaseConfigStr) : {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
setPersistence(auth, browserSessionPersistence);
const googleAuthProvider = new GoogleAuthProvider();
const facebookAuthProvider = new FacebookAuthProvider();
const microsoftAuthProvider = new OAuthProvider('microsoft.com');

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUser(user);
        });
        return () => unsubscribe();
    }, []);

    return (
        <AuthContext.Provider value={{ user }}>
            {children}
        </AuthContext.Provider>
    );
};

// Probe whether `password` is the account's current password, using a throwaway
// secondary app so we never disturb the primary session or its auth-state listener.
const isCurrentPassword = async (email, password) => {
    const probeApp = initializeApp(firebaseConfig, 'pw-reuse-probe');
    const probeAuth = getAuth(probeApp);
    try {
        await setPersistence(probeAuth, inMemoryPersistence);
        await signInWithEmailAndPassword(probeAuth, email, password);
        return true;
    } catch {
        return false;
    } finally {
        try { await signOut(probeAuth); } catch { /* ignore */ }
        await deleteApp(probeApp);
    }
};

export {
    auth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithRedirect,
    getRedirectResult,
    signInWithPopup,
    signOut,
    googleAuthProvider,
    onAuthStateChanged,
    onIdTokenChanged,
    facebookAuthProvider,
    microsoftAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    applyActionCode,
    checkActionCode,
    confirmPasswordReset,
    verifyPasswordResetCode,
    // Re-authenticate + change-password — only meaningful for users who
    // signed up with the email/password provider (Google/Microsoft/etc.
    // users don't have a Firebase-managed password to update).
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
    isCurrentPassword,
};